/* @jsx h */
import { defineComponent, h } from "vue";
import type { TimeOfDay } from "./types";

interface TimeFieldProps {
  value: TimeOfDay;
  ariaLabel: string;
  disabled?: boolean;
  onChange: (value: TimeOfDay) => void;
}

/**
 * `<input type="time">` in an explicit bordered chip. Native gives us the
 * platform time picker inside the Expo WebView, correct 12h/24h presentation
 * per locale, and keyboard entry on desktop; the chip is what makes it read as
 * editable — bare, it looks like a caption under the date and nobody clicks it.
 *
 * The native picker indicator is deliberately kept (rather than swapped for our
 * own leading icon) because on desktop it is the only control that opens the
 * dropdown picker.
 */
export const TimeField = defineComponent<TimeFieldProps>({
  name: "TimeField",
  props: ["value", "ariaLabel", "disabled", "onChange"],
  setup(props) {
    return () => (
      <label
        class={[
          "group flex w-full items-center gap-1 rounded-lg border px-2 py-[3px]",
          "transition-colors duration-150",
          props.disabled
            ? "cursor-not-allowed  opacity-50"
            : [
                "cursor-pointer /80 bg-base-300",
                "hover:border-primary/60",
                "focus-within:border-primary focus-within:ring-1 focus-within:ring-primary",
              ].join(" "),
        ].join(" ")}
      >
        <input
          type="time"
          value={props.value}
          aria-label={props.ariaLabel}
          disabled={props.disabled}
          onChange={(e: Event) => {
            const next = (e.target as HTMLInputElement).value;
            // Clearing the field yields "" in some browsers, which would make
            // the range unserialisable — keep the previous value instead.
            if (next) props.onChange(next);
          }}
          class={[
            "w-full min-w-0 cursor-pointer bg-transparent",
            "text-[13px] font-medium tabular-nums text-base-content/85 outline-none",
            "[&::-webkit-calendar-picker-indicator]:opacity-70",
            "[&::-webkit-calendar-picker-indicator]:group-hover:opacity-100",
            "[&::-webkit-calendar-picker-indicator]:cursor-pointer",
          ].join(" ")}
        />
      </label>
    );
  },
});
